import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from '../newConversation.translations'

import { useShoppingAssistantHost } from './useShoppingAssistantHost'
import type { NewChatDialog } from './useShoppingAssistantHost.injection-key'

function buildDialog(
  i18n: ReturnType<typeof useI18n>,
  acceptButtonText: string,
  onAccept: () => void,
): NewChatDialog {
  return {
    title: i18n(translations.title),
    description: i18n(translations.description),
    acceptButtonText,
    declineButtonText: i18n(translations.declineButtonText),
    onAccept,
  }
}

/**
 * Shared dialog-confirmation logic for "start a new conversation" and "close"
 * CTAs. Both share title, description, and decline button text; the accept
 * button text differs (new chat vs. close), so `buildCloseDialog` takes it as
 * a parameter.
 *
 * `useNewConversationDialog` calls `useShoppingAssistantHost()` and is meant
 * for components that inject the host (Header, ChatHistoryModal,
 * ShoppingAssistantChat). Host adapter composables must use
 * `useCloseDialog` instead — they are creating the host, so injecting it would
 * be circular.
 */
export function useNewConversationDialog() {
  const i18n = useI18n()
  const { newChat } = useShoppingAssistantHost()

  function requestNewChat(onAccept: () => void) {
    newChat(buildDialog(i18n, i18n(translations.acceptButtonText), onAccept))
  }

  return { requestNewChat }
}

/**
 * Close-confirmation dialog builder for host adapters. Same shared i18n fields
 * as `useNewConversationDialog`, but without the `useShoppingAssistantHost()`
 * dependency — safe to call from within a host composable that is itself
 * creating the host.
 */
export function useCloseDialog() {
  const i18n = useI18n()

  function buildCloseDialog(
    acceptButtonText: string,
    onAccept: () => void,
  ): NewChatDialog {
    return buildDialog(i18n, acceptButtonText, onAccept)
  }

  return { buildCloseDialog }
}
